import React from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle, FaSave } from "react-icons/fa";
import type {
  MutationProcessingMode,
  MutationProcessingSetting,
  MutationProcessingUpdatePayload,
} from "../types/mutationProcessing.types";

const processingModes: MutationProcessingMode[] = ["MANUAL", "AUTOMATIC", "AUTOMATIC_WITH_REVIEW"];

const selectClass =
  "h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10";

const switchTrack = (checked: boolean, disabled: boolean) =>
  [
    "relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition",
    checked
      ? "border-emerald-500 bg-emerald-500 dark:border-emerald-400 dark:bg-emerald-500"
      : "border-slate-300 bg-slate-200 dark:border-white/15 dark:bg-slate-700",
    disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
  ].join(" ");

interface MutationProcessingRowProps {
  setting: MutationProcessingSetting;
  canUpdate: boolean;
  saving: boolean;
  animationDelayMs?: number;
  onUpdate: (setting: MutationProcessingSetting, payload: MutationProcessingUpdatePayload) => void;
}

export const MutationProcessingRow: React.FC<MutationProcessingRowProps> = ({
  setting,
  canUpdate,
  saving,
  animationDelayMs = 0,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const disabled = !canUpdate || saving;
  const statusLabel = setting.enabled
    ? t("admin.mutation_processing.enabled", { defaultValue: "Enabled" })
    : t("admin.mutation_processing.disabled", { defaultValue: "Disabled" });

  return (
    <article
      className="lumen-slide-page group grid gap-4 rounded-2xl border border-white/70 bg-white/95 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur transition duration-300 hover:-translate-y-0.5 hover:border-indigo-100 hover:shadow-[0_24px_70px_rgba(15,23,42,0.13)] motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-slate-950/80 dark:hover:border-indigo-400/25 dark:hover:shadow-[0_24px_70px_rgba(0,0,0,0.45)] md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center [--lumen-slide-page-offset:18px]"
      style={{ animationDelay: `${animationDelayMs}ms` }}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-bold text-slate-950 dark:text-white">
            {t(`admin.mutation_processing.types.${setting.mutationType}.name`, {
              defaultValue: setting.mutationType,
            })}
          </h3>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-600 transition group-hover:border-indigo-100 group-hover:bg-indigo-50/70 group-hover:text-indigo-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:group-hover:border-indigo-400/20 dark:group-hover:bg-indigo-400/10 dark:group-hover:text-indigo-200">
            {statusLabel}
          </span>
        </div>
        <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {t(`admin.mutation_processing.types.${setting.mutationType}.description`, {
            defaultValue: "Mutation processing setting.",
          })}
        </p>
        {setting.manualOnly && (
          <div className="mt-3 inline-flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 dark:border-amber-500/40 dark:bg-amber-900/25 dark:text-amber-100">
            <FaExclamationTriangle className="mt-0.5 shrink-0" />
            {t("admin.mutation_processing.manual_only_warning", {
              defaultValue: "Sensitive mutation type. Fully automatic processing is not available.",
            })}
          </div>
        )}
      </div>

      <label className="flex items-center justify-between gap-3 text-sm font-bold text-slate-700 dark:text-slate-200 md:justify-start">
        <span>{t("admin.mutation_processing.automatic_processing", { defaultValue: "Automatic processing" })}</span>
        <input
          type="checkbox"
          checked={Boolean(setting.enabled)}
          disabled={disabled}
          onChange={(event) =>
            onUpdate(setting, {
              enabled: event.target.checked,
              processingMode: setting.processingMode,
            })
          }
          className="sr-only"
          aria-label={t("admin.mutation_processing.automatic_processing_for", {
            defaultValue: "Automatic processing for {{type}}",
            type: t(`admin.mutation_processing.types.${setting.mutationType}.name`, {
              defaultValue: setting.mutationType,
            }),
          })}
        />
        <span className={switchTrack(Boolean(setting.enabled), disabled)} aria-hidden="true">
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition duration-200 ${
              setting.enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </span>
      </label>

      <div className="flex items-center gap-2 md:justify-end">
        <select
          aria-label={t("admin.mutation_processing.processing_mode", { defaultValue: "Processing mode" })}
          value={setting.processingMode}
          disabled={disabled}
          onChange={(event) =>
            onUpdate(setting, {
              enabled: setting.enabled,
              processingMode: event.target.value as MutationProcessingMode,
            })
          }
          className={selectClass}
        >
          {processingModes.map((mode) => (
            <option key={mode} value={mode} disabled={setting.manualOnly && mode === "AUTOMATIC"}>
              {t(`admin.mutation_processing.modes.${mode}`, { defaultValue: mode })}
            </option>
          ))}
        </select>
        {saving && (
          <FaSave
            aria-label={t("admin.mutation_processing.saving", { defaultValue: "Saving" })}
            className="animate-pulse text-indigo-600 dark:text-indigo-300"
          />
        )}
      </div>
    </article>
  );
};
